import { ArrayScreenFormat } from "@glide/app-description";
import { Mood, TextComponentStyle, type SuperTableTextCell } from "@glide/component-utils";
import type {
    WireSuperTableListComponent,
    WireSuperTableColumn,
    WireSuperTableRow,
} from "@glide/fluent-components/dist/js/base-components";
import type {
    WireBigNumbersComponent,
    WireHintComponent,
    WireListCardCollection,
    WireRenderableContentComponent,
    WireRichTextComponent,
    WireSimpleImageComponent,
    WireTextComponent,
} from "@glide/fluent-components/dist/js/fluent-components";
import { mapFilterUndefined } from "@glideapps/ts-necessities";
import { logError } from "@glide/support";
import {
    CardStyle,
    UIAlignment,
    UIAspect,
    UIImageFill,
    UIImageStyle,
    UIOrientation,
    UISize,
    UIStyleVariant,
    UITitleStyle,
    WireComponentKind,
    type WireComponent,
    type WirePagedGroup,
    UILayoutVariant,
} from "@glide/wire";
import {
    componentRenderableDescriptionCodec,
    renderableDescriptionsCodec,
    type BigNumbersRenderableDescription,
    type ComponentRenderableDescription,
    type HintRenderableDescription,
    type ImageRenderableDescription,
    type ListRenderableDescription,
    type RichTextRenderableDescription,
    type TableRenderableDescription,
    type TextRenderableDescription,
} from "@glide/fluent-components/dist/js/fluent-components-types";
import { isRight } from "fp-ts/lib/Either";

export function getComponentsFromRenderableDescriptions(
    rawDescriptions: WireRenderableContentComponent["descriptions"]
): readonly WireComponent[] {
    const renderableDescriptions = getRenderableDescriptions(rawDescriptions);
    if (renderableDescriptions.length > 0) {
        return mapFilterUndefined(renderableDescriptions, desc => getWireComponentFromRenderableDescription(desc));
    }
    return [];
}

function getRenderableDescriptions(
    rawDescriptions: WireRenderableContentComponent["descriptions"]
): ComponentRenderableDescription[] {
    if (rawDescriptions === null || rawDescriptions === undefined) {
        return [];
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(rawDescriptions);
    } catch (err: unknown) {
        logError(err);
        return [
            {
                kind: "hint",
                text: "Could not parse Glide components from your descriptions",
                mood: Mood.Warning,
            },
        ];
    }

    const decoded = renderableDescriptionsCodec.decode(parsed);
    if (isRight(decoded)) {
        return decoded.right;
    }

    // If the array as a whole doesn't decode, try to salvage individual items
    if (Array.isArray(parsed)) {
        return parsed.flatMap(item => {
            const itemDecoded = componentRenderableDescriptionCodec.decode(item);
            if (isRight(itemDecoded)) {
                return [itemDecoded.right];
            }
            logError(`Invalid renderable description: ${JSON.stringify(item)}`);
            return [];
        });
    }

    // Try to decode as a single component spec (not wrapped in array)
    const singleDecoded = componentRenderableDescriptionCodec.decode(parsed);
    if (isRight(singleDecoded)) {
        return [singleDecoded.right];
    }

    logError("Renderable descriptions is not an array or valid component");
    return [
        {
            kind: "hint",
            text: "Renderable descriptions must be an array or a valid component",
            mood: Mood.Warning,
        },
    ];
}

function getWireComponentFromRenderableDescription(
    description: ComponentRenderableDescription
): WireComponent | undefined {
    const { kind } = description;
    switch (kind) {
        case "big-numbers":
            return getBigNumbersComponentFromRenderableDescription(description);

        case "hint":
            return getHintComponentFromRenderableDescription(description);

        case "image":
            return getImageComponentFromRenderableDescription(description);

        case "list":
            return getListComponentFromRenderableDescription(description);

        case "text":
            return getTextComponentFromRenderableDescription(description);

        case "table":
            return getTableComponentFromRenderableDescription(description);

        case "rich-text":
            return getRichTextComponentFromRenderableDescription(description);

        default:
            logError(`Unexpected renderable kind: ${kind}`);
            return undefined;
    }
}

type WireBigNumbersItem = WireBigNumbersComponent["data"][number];

function getBigNumbersComponentFromRenderableDescription(
    structuredDescription: BigNumbersRenderableDescription
): WireBigNumbersComponent {
    const { items, title } = structuredDescription;
    return {
        kind: WireComponentKind.BigNumbers,
        title,
        data: items.map(item => {
            const bigNumberItem: WireBigNumbersItem = {
                name: item.name,
                value: item.value,
                description: item.description,
            };
            return bigNumberItem;
        }),
    };
}

function getTextComponentFromRenderableDescription(
    structuredDescription: TextRenderableDescription
): WireTextComponent {
    return {
        kind: WireComponentKind.Text,
        text: structuredDescription.text,
        style: (structuredDescription.style ?? TextComponentStyle.regular),
        align: UIAlignment.Start,
    };
}

function getHintComponentFromRenderableDescription(
    structuredDescription: HintRenderableDescription
): WireHintComponent {
    return {
        kind: WireComponentKind.Hint,
        description: structuredDescription.text,
        mood: structuredDescription.mood ?? Mood.Default,
    };
}

function getImageComponentFromRenderableDescription(
    structuredDescription: ImageRenderableDescription
): WireSimpleImageComponent {
    return {
        kind: WireComponentKind.SimpleImage,
        image: structuredDescription.image,
        aspectRatio: structuredDescription.aspectRatio ?? UIAspect.Auto,
        align: UIAlignment.Center,
        style: structuredDescription.style ?? UIImageStyle.Rectilinear,
        size: structuredDescription.size ?? UISize.Full,
        fullWidthOnMobile: structuredDescription.fullWidthOnMobile ?? false,
        zoomable: structuredDescription.zoomable ?? false,
        imageFill: structuredDescription.imageFill ?? UIImageFill.Cover,
    };
}

type WireListItem = WireListCardCollection["groups"][number]["items"][number];

function getListComponentFromRenderableDescription(
    structuredDescription: ListRenderableDescription
): WireListCardCollection {
    const { title, items } = structuredDescription;

    return {
        kind: WireComponentKind.List,
        format: ArrayScreenFormat.CardCollection,
        componentTitle: title,
        cardStyle: CardStyle.List,
        size: UISize.Medium,
        style: UIStyleVariant.Minimal,
        cardCollectionComponentStyle: UIStyleVariant.Minimal,
        imageStyle: UIImageStyle.Rectilinear,
        imageSize: UISize.Medium,
        alignment: UIAlignment.Start,
        imageFill: UIImageFill.Cover,
        aspectRatio: UIAspect.Square,
        orientation: UIOrientation.Vertical,
        showSeeAll: false,
        emptyMessage: "",
        titleStyle: UITitleStyle.Simple,
        titleActions: [],
        groups: [
            {
                title: "",
                groupKey: "",
                paging: {
                    pageSize: items.length,
                    numPages: 1,
                    itemsCount: items.length,
                    pageIndex: {
                        value: 0,
                        onChangeToken: undefined,
                    },
                },
                items: items.map(item => {
                    const listItem: WireListItem = {
                        title: item.title,
                        subtitle: item.subtitle,
                        emphasis: item.emphasis,
                        image: item.image,
                        itemActions: [],
                    };

                    return listItem;
                }),
            },
        ],
        layoutVariant: UILayoutVariant.Default,
    };
}

function getTableComponentFromRenderableDescription(
    structuredDescription: TableRenderableDescription
): WireSuperTableListComponent | undefined {
    const { title, items } = structuredDescription;

    if (items.length === 0) {
        return undefined;
    }

    const columnKeySet = new Set<string>();
    for (const item of items) {
        for (const key of Object.keys(item)) {
            columnKeySet.add(key);
        }
    }
    const columnKeys = [...columnKeySet];

    const columns: WireSuperTableColumn[] = columnKeys.map(columnName => {
        return {
            kind: "text" as const,
            header: columnName,
            sizeOptions: { sizeKind: "auto" as const },
            columnName,
            headerAction: undefined,
        };
    });

    const rows: WireSuperTableRow[] = items.map(item => {
        return columnKeys.map((key): SuperTableTextCell => {
            const value = item[key];
            return {
                kind: "text",
                value: value !== undefined ? String(value) : "",
                accessoryImage: undefined,
                appID: "",
            };
        });
    });

    const group: WirePagedGroup<WireSuperTableRow> = {
        title: "",
        groupKey: "",
        items: rows,
        paging: {
            pageSize: items.length,
            numPages: 1,
            itemsCount: items.length,
            pageIndex: {
                value: 0,
                onChangeToken: undefined,
            },
        },
    };

    return {
        kind: WireComponentKind.List,
        format: ArrayScreenFormat.SuperTable,
        title: title ?? "",
        titleActions: [],
        columns,
        groups: [group],
        activeSort: undefined,
        style: "minimal",
    };
}

function getRichTextComponentFromRenderableDescription(
    structuredDescription: RichTextRenderableDescription
): WireRichTextComponent {
    return {
        kind: WireComponentKind.RichText,
        text: structuredDescription.text,
    };
}
